"use client";

import { useCallback, useMemo } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "@/shared/feedback";
import type { ContentItemAction } from "@/shared/ui/display/ContentItemPrimitives";

export type ResolvedSuggestedThoughtAction = ContentItemAction;

type UseSuggestedThoughtItemActionsParams = {
  thoughtText: string;
  confirmActionLabel: string;
  confirmActionHumanReadable: string;
  onConfirmAction: () => void | Promise<void>;
  onDeleteAction: () => void | Promise<void>;
  isConfirmEmphasized?: boolean;
  onEditAction?: () => void | Promise<void>;
};

export default function useSuggestedThoughtItemActions({
  thoughtText,
  confirmActionLabel,
  confirmActionHumanReadable,
  onConfirmAction,
  onDeleteAction,
  isConfirmEmphasized = false,
  onEditAction,
}: UseSuggestedThoughtItemActionsParams) {
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(thoughtText);
      showSuccessToast("Copied to clipboard");
    } catch {
      showErrorToast("Failed to copy thought");
    }
  }, [thoughtText]);

  return useMemo<ResolvedSuggestedThoughtAction[]>(
    () => [
      {
        key: "confirm",
        label: confirmActionLabel,
        humanReadable: confirmActionHumanReadable,
        icon: "confirm",
        onClick: onConfirmAction,
        color: "moveon.primary",
        emphasized: isConfirmEmphasized,
      },
      ...(onEditAction
        ? [
            {
              key: "edit",
              label: "Edit thought",
              humanReadable: "Edit suggested thought",
              icon: "edit" as const,
              onClick: onEditAction,
            },
          ]
        : []),
      {
        key: "copy",
        label: "Copy thought",
        humanReadable: "Copy thought to clipboard",
        icon: "copy_clipboard",
        onClick: handleCopy,
      },
      {
        key: "delete",
        label: "Delete suggestion",
        humanReadable: "Delete suggested thought permanently",
        icon: "delete_permanent",
        onClick: onDeleteAction,
        tone: "danger" as const,
      },
    ],
    [
      confirmActionHumanReadable,
      confirmActionLabel,
      handleCopy,
      isConfirmEmphasized,
      onConfirmAction,
      onDeleteAction,
      onEditAction,
    ],
  );
}
